/* eslint-disable sonarjs/no-duplicate-string */
import { ContainerComponent, createContainer, createHook, createStore, StoreActionApi } from 'react-sweet-state'
import type { ApprovabilityRequest, ApprovabilityResult, Institution } from 'generated/api'
import { getCalculatorClient } from 'services/FionaAPI/Calculator/client'
import { Empty, wait } from 'lib'
import type { NextRouter } from 'next/router'
import { addDDError } from 'services/AnalyticsLite/Datadog/events'
import { useDebounce } from 'rooks'
import institutionUuids from './assets/institutionsList.json'
import { UrlObject } from 'url'
import { ga4Client } from 'services/AnalyticsLite/GA4/utils/client'
import { PropsWithChildren } from 'react'

const createApplyUrlFromApprovability = (request: Partial<ApprovabilityRequest>): UrlObject => {
	return {
		pathname: '/products/loans',
		query: {
			loanAmount: request.loanAmount,
			creditRating: request.creditScore,
			annualIncome: request.annualIncome,
			loanPurpose: request.purpose,
			productType: 'loan',
			prepopMode: 'inline'
		}
	}
}

export type LoadingApprovability = ApprovabilityResult & {
	institutions?: Array<Institution & { loading?: true | never }>
}
interface State {
	loading: boolean
	result: LoadingApprovability
	prepop: Partial<ApprovabilityRequest>
	view: 'help' | 'calculator'
}
const emptyLender = (uuid: string, image: string): Institution & { loading?: true | never } => ({
	loading: true,
	provider: {
		description: '',
		disclaimer: '',
		imageUrl: image,
		name: '',
		status: undefined,
		supportsPersonalizedOffers: false,
		uuid: uuid,
		supportsPreSelect: false, // TODO is this correct?
		shouldDisplayPreSelect: false // TODO is this correct?
	},
	summary: { aprRange: { max: 0, min: 0 }, level: 'UNKNOWN' }
})
const initialState: State = {
	loading: true,
	result: {
		institutions: Array.from(institutionUuids).map(value => emptyLender(value.uuid, value.image)),
		summary: { aprRange: { max: 0, min: 0 }, level: 'UNKNOWN' }
	},
	view: 'calculator',
	prepop: {}
}

const beginLoadingTransition = ({ setState, getState }: StoreActionApi<State>) => {
	if (!getState().loading) {
		setState({
			result: {
				...getState().result,
				institutions: getState().result.institutions?.map(item => ({ ...item, loading: true }))
			},
			loading: true
		})
	}
}

const getData = async (formData: ApprovabilityRequest) => {
	const results = await getCalculatorClient().getApprovability({ approvabilityRequest: formData })
	const mappedValue =
		results.institutions?.map(x => ({ [x.provider.uuid]: x })).reduce((cur, acc) => ({ ...cur, ...acc })) || {}
	return {
		...results,
		institutions: Array.from(institutionUuids).map(value => mappedValue[value.uuid])
	}
}

const Store = createStore({
	initialState,
	actions: {
		beginLoadingTransition: () => beginLoadingTransition,
		fetchNewResults:
			(formData: ApprovabilityRequest, userTriggered = true) =>
			({ setState }) => {
				if (userTriggered) {
					ga4Client.eventsClient.track({
						name: 'approvability_requested',
						params: { experience: 'tools:approvability' }
					})
				}
				Promise.all([getData(formData), wait(0, '')])
					.then(([results]) => setState({ result: results, prepop: formData, loading: false }))
					.catch(addDDError)
			},
		changeView:
			(view: State['view']) =>
			({ setState }) => {
				ga4Client.eventsClient.track({ name: 'help_toggled', params: { experience: 'tools:approvability' } })
				setState({ view: view })
			},
		apply:
			(router: NextRouter) =>
			async ({ getState }) => {
				ga4Client.eventsClient.track({ name: 'start_application', params: { experience: 'tools:approvability' } })
				await router.push(createApplyUrlFromApprovability(getState().prepop))
			}
	},
	name: 'Approvability State'
})

export const ApprovabilityDataContainer: ContainerComponent<PropsWithChildren<Empty>> = createContainer(Store, {
	onInit:
		() =>
		({ dispatch }) => {
			dispatch(
				Store.actions.fetchNewResults({
					annualIncome: 800000,
					creditScore: 'good',
					loanAmount: 10000
				})
			)
		}
})

export const useApprovabilityControls = createHook(Store, { selector: null })
export const useApprovabilityData = createHook(Store, {
	selector: ({ result, loading, prepop }) => ({ ...result, loading, prepop })
})
export const useViewState = createHook(Store, { selector: ({ view }) => view })
export const useDebouncedApprovabilityRequest = () => {
	const [, { fetchNewResults, beginLoadingTransition }] = useApprovabilityControls()
	const debouncedCall = useDebounce(fetchNewResults, 800)
	return (data: Parameters<typeof fetchNewResults>[0]) => {
		beginLoadingTransition()
		debouncedCall(data)
	}
}
